import "./FilterInput.scss";

import React, { useCallback, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import ReactFilterBox, {
	GridDataAutoCompleteHandler,
	SimpleResultProcessing,
} from "react-filter-box";
import clsx from "clsx";

import { TextElement } from "../../../lib/util/LocaleUtils";
import Text from "../../common/Text/Text";

export interface FilterInputOption {
	type: "text" | "selection";
	columnText?: string;
	columnField: string;
}

interface BaseFilterExpressionsParseResult {
	success: boolean;
	error?: FilterExpressionsParseError;
	expressions?: FilterExpression[];
}

export interface FilterExpressionsParseError {
	name: string;
	message: string;
	isError: true;
	found: any;
}

export interface FilterExpression {
	category: string;
	operator: string;
	value: string;
}

interface FilterExpressionsParseResultSuccess
	extends BaseFilterExpressionsParseResult {
	success: true;
	error: undefined;
	expressions: FilterExpression[];
}

interface FilterExpressionsParseResultFailed
	extends BaseFilterExpressionsParseResult {
	success: false;
	error: FilterExpressionsParseError;
	expressions: undefined;
}

export type FilterExpressionsParseResult =
	| FilterExpressionsParseResultFailed
	| FilterExpressionsParseResultSuccess;

export interface FilterInputProps {
	value?: string;
	defaultValue?: string;
	className?: string;
	items: any[];
	options: FilterInputOption[];
	placeholder?: TextElement;
	onChange?: (value: string, result: FilterExpressionsParseResult) => void;
}

class CustomAutoComplete extends GridDataAutoCompleteHandler {
	needOperators(parsedCategory: string) {
		const result = super.needOperators(parsedCategory);
		return result.concat(["startsWith", "is", "isn't", "equals"]);
	}
}

export class ResultProcessing extends SimpleResultProcessing {
	// override this method
	filter(row: any, fieldOrLabel: string, operator: string, value: string) {
		const field = this.tryToGetFieldCategory(fieldOrLabel);
		const fieldValue = row[field] || "";

		let parsedValue = value;
		if (parsedValue === "null") {
			parsedValue = "";
		}

		switch (operator) {
			case "==":
			case "is":
			case "equals":
				// eslint-disable-next-line eqeqeq
				return fieldValue == parsedValue;
			case "isn't":
			case "!is":
			case "!equals":
			case "!=":
				// eslint-disable-next-line eqeqeq
				return fieldValue != parsedValue;
			case "contains":
				return fieldValue.toLowerCase().indexOf(parsedValue.toLowerCase()) >= 0;
			case "!contains":
				return fieldValue.toLowerCase().indexOf(parsedValue.toLowerCase()) < 0;
			case "startsWith":
				return fieldValue.toLowerCase().startsWith(parsedValue.toLowerCase());
		}

		return false;
	}
}

const FilterInput = (props: FilterInputProps) => {
	const { className, items, placeholder, onChange, options, ...otherProps } =
		props;
	const [value, setValue] = useState<string | undefined>(undefined);

	const handleChange = useCallback(
		(value: string, result: any) => {
			if (onChange) {
				const parsedResult: FilterExpressionsParseResult = {
					success: !result.isError,
					error: result.isError ? result : undefined,
					expressions: result.isError ? undefined : result,
				};
				onChange(value, parsedResult);
			}
			setValue(value);
		},
		[onChange],
	);

	const editorConfig = useMemo(
		() => ({
			lineWrapping: true,
			scrollbarStyle: "null",
		}),
		[],
	);

	const ref = useRef<ReactFilterBox | null>(null);

	const autoComplete = useMemo(
		() => new CustomAutoComplete(items, options),
		[items, options],
	);

	const handleClick = useCallback(() => {
		(ReactDOM.findDOMNode(ref.current) as HTMLDivElement)
			.querySelector("textarea")!
			.focus();
	}, []);

	return (
		<div className={clsx("FilterInput", className)} onClick={handleClick}>
			{!value && placeholder && (
				<Text className={"Field__input-placeholder"}>{placeholder}</Text>
			)}
			<ReactFilterBox
				ref={ref}
				autoCompleteHandler={autoComplete}
				editorConfig={editorConfig}
				options={options}
				onChange={handleChange}
				{...otherProps}
			/>
		</div>
	);
};

export default FilterInput;
